<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { pdfRequest, pdfStatus, resetPdfExport } from '$stores/pdf.js';
  import { manifest } from '$stores/content.js';
  import type { VolumeItem, ArticleContent, TableOfContentsItem } from '$types/content.js';

  // React to pdfRequest changes
  $: if ($pdfRequest) {
    startExport($pdfRequest.volumePath, $pdfRequest.volumeName);
  }

  // ── Article collection ────────────────────────────────────────────────────

  interface ArticleOrder {
    mainPaths: string[];  // glossary + regular articles
    wikiPaths: string[];  // volume wiki subfolder articles
  }

  function collectArticleOrder(volumes: VolumeItem[], volumePath: string): ArticleOrder {
    const prefix = volumePath + '/';
    const items = volumes.filter(v => v.path.startsWith(prefix));

    const glossary  = items.find(v => v.path === volumePath + '/glossary');
    const wikiItems = items
      .filter(v => v.path.startsWith(volumePath + '/wiki/'))
      .sort((a, b) => a.order - b.order);
    const regular   = items
      .filter(v => v !== glossary && !v.path.startsWith(volumePath + '/wiki/'))
      .sort((a, b) => a.order - b.order);

    return {
      mainPaths: [
        ...(glossary ? ['volumes/' + glossary.path] : []),
        ...regular.map(v => 'volumes/' + v.path),
      ],
      wikiPaths: wikiItems.map(v => 'volumes/' + v.path),
    };
  }

  // ── Fetching ──────────────────────────────────────────────────────────────

  interface ArticleData {
    path: string;
    html: string;
    title: string;
    toc: TableOfContentsItem[];
  }

  async function fetchAllArticles(paths: string[]): Promise<ArticleData[]> {
    const BATCH = 6;
    const results: ArticleData[] = new Array(paths.length);
    let done = 0;

    for (let i = 0; i < paths.length; i += BATCH) {
      const batch = paths.slice(i, i + BATCH);
      const fetched = await Promise.all(
        batch.map(async (p, bi) => {
          const res = await fetch(`/articles/${p}.json`);
          if (!res.ok) throw new Error(`Failed to fetch /articles/${p}.json`);
          const data = await res.json();
          return { path: p, html: data.html, title: data.title, toc: data.toc ?? [] };
        })
      );
      for (let bi = 0; bi < fetched.length; bi++) {
        results[i + bi] = fetched[bi];
      }
      done += fetched.length;
      pdfStatus.set({ phase: 'fetching', done, total: paths.length });
    }

    return results;
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────

  function prefixIds(root: Element, prefix: string): void {
    // Prefix all [id] elements
    for (const el of Array.from(root.querySelectorAll('[id]'))) {
      el.id = prefix + el.id;
    }
    // Fix same-page anchor links (not SPA links like #/)
    for (const a of Array.from(root.querySelectorAll('a[href^="#"]'))) {
      const raw = a.getAttribute('href')!;
      if (!raw.startsWith('#/')) {
        a.setAttribute('href', '#' + prefix + raw.slice(1));
      }
    }
  }

  function rewriteLinks(
    root: Element,
    articleIndexMap: Map<string, number>
  ): void {
    for (const a of Array.from(root.querySelectorAll('a[href]'))) {
      const raw = a.getAttribute('href')!;

      if (raw.startsWith('http://') || raw.startsWith('https://')) {
        // External link — keep as-is
        continue;
      }

      if (raw.startsWith('#/')) {
        const afterSlash = raw.slice(2); // "volumes/00-authors-guide/03-directives" or "volumes/...#anchor"
        const hashPos = afterSlash.indexOf('#');
        let base: string;
        let inPageAnchor: string | null;

        if (hashPos >= 0) {
          base = '#/' + afterSlash.slice(0, hashPos);
          inPageAnchor = afterSlash.slice(hashPos + 1);
        } else {
          base = raw;
          inPageAnchor = null;
        }

        const idx = articleIndexMap.get(base);
        if (idx !== undefined) {
          const dest = inPageAnchor
            ? `#art-${idx}-${inPageAnchor}`
            : `#art-${idx}-toc-article`;
          a.setAttribute('href', dest);
        } else {
          // Not in this PDF (cross-volume / wiki link) — strip the link, keep the text.
          // Converting to an absolute URL would cause print CSS to append the full URL.
          const span = document.createElement('span');
          while (a.firstChild) span.appendChild(a.firstChild);
          a.replaceWith(span);
        }
        continue;
      }

      // Anchors starting with "#" (not "#/") were already handled by prefixIds
    }
  }

  function removeInteractiveElements(root: Element): void {
    const selectors = [
      '.roll-table-controls',
      '.article-nav',
      '.link-preview',
    ];
    for (const sel of selectors) {
      for (const el of Array.from(root.querySelectorAll(sel))) {
        el.remove();
      }
    }
  }

  function revealSpoilers(root: Element): void {
    // Simple spoilers: remove overlay, force content visible
    for (const el of Array.from(root.querySelectorAll('.spoiler:not(.spoiler-encrypted)'))) {
      const overlay = el.querySelector('.spoiler-overlay');
      if (overlay) overlay.remove();
      const content = el.querySelector('.spoiler-content') as HTMLElement | null;
      if (content) {
        content.style.display = 'block';
        content.style.visibility = 'visible';
        content.style.maxHeight = 'none';
        content.style.opacity = '1';
      }
      const toggle = el.querySelector('.spoiler-toggle');
      if (toggle) toggle.remove();
    }

    // Encrypted spoilers: strip password form, leave static message
    for (const el of Array.from(root.querySelectorAll('.spoiler-encrypted'))) {
      const form = el.querySelector('.spoiler-password-form');
      if (form) form.remove();
      // Ensure the static overlay message is visible
      const overlay = el.querySelector('.spoiler-overlay') as HTMLElement | null;
      if (overlay) {
        overlay.style.display = 'flex';
      }
    }
  }

  function replaceSkilltrees(
    root: Element,
    articleIndexMap: Map<string, number>
  ): void {
    for (const embed of Array.from(root.querySelectorAll('.skilltree-embed'))) {
      const rawData = embed.getAttribute('data-skilltree');
      if (!rawData) {
        embed.remove();
        continue;
      }

      let parsed: { nodes?: Array<{ id: string; label: string; path?: string }>; edges?: Array<{ from: string; to: string }> };
      try {
        parsed = JSON.parse(rawData);
      } catch {
        embed.remove();
        continue;
      }

      const nodes = parsed.nodes || [];
      const edges = parsed.edges || [];

      // Build adjacency: parent id → child ids[]
      const childrenOf = new Map<string, string[]>(nodes.map(n => [n.id, []]));
      const hasParent = new Set<string>();
      for (const edge of edges) {
        if (!childrenOf.has(edge.from)) childrenOf.set(edge.from, []);
        childrenOf.get(edge.from)!.push(edge.to);
        hasParent.add(edge.to);
      }

      const nodeById = new Map(nodes.map(n => [n.id, n]));
      const roots = nodes.filter(n => !hasParent.has(n.id));

      function makeNodeLink(node: { id: string; label: string; path?: string }): HTMLElement {
        const validPath = node.path && node.path !== '#';
        if (!validPath) {
          const span = document.createElement('span');
          span.textContent = node.label;
          return span;
        }
        const a = document.createElement('a');
        a.textContent = node.label;
        const spaKey = '#' + node.path;
        const idx = articleIndexMap.get(spaKey);
        if (idx !== undefined) {
          a.setAttribute('href', `#art-${idx}-toc-article`);
        } else {
          // Not in this PDF — strip link, keep text
          const span = document.createElement('span');
          span.textContent = node.label;
          return span;
        }
        return a;
      }

      function buildTree(nodeId: string): HTMLLIElement {
        const node = nodeById.get(nodeId) ?? { id: nodeId, label: nodeId };
        const li = document.createElement('li');
        li.appendChild(makeNodeLink(node));
        const children = childrenOf.get(nodeId) ?? [];
        if (children.length > 0) {
          const ul = document.createElement('ul');
          for (const childId of children) ul.appendChild(buildTree(childId));
          li.appendChild(ul);
        }
        return li;
      }

      const container = document.createElement('div');
      container.className = 'skilltree-preview-compact';
      const ul = document.createElement('ul');
      for (const rootNode of roots) ul.appendChild(buildTree(rootNode.id));
      container.appendChild(ul);
      embed.replaceWith(container);
    }
  }

  // ── Image embedding ───────────────────────────────────────────────────────

  function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async function embedImages(root: Element): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
      imgs.map(async (img) => {
        const rawSrc = img.getAttribute('src');
        if (!rawSrc || rawSrc.startsWith('data:')) return;
        try {
          const absoluteSrc = new URL(rawSrc, window.location.href).href;
          const res = await fetch(absoluteSrc);
          if (!res.ok) return;
          const dataUrl = await blobToDataUrl(await res.blob());
          img.setAttribute('src', dataUrl);
        } catch {
          // Leave src unchanged; browser may still print from cache
        }
      })
    );
  }

  // ── Per-article HTML assembly ─────────────────────────────────────────────

  function buildArticleSection(
    article: ArticleData,
    idx: number,
    articleIndexMap: Map<string, number>
  ): HTMLDivElement {
    const prefix = `art-${idx}-`;

    // Parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div class="markdown-content">${article.html}</div>`,
      'text/html'
    );
    const root = doc.querySelector('.markdown-content')!;

    // Pipeline
    prefixIds(root, prefix);
    rewriteLinks(root, articleIndexMap);
    removeInteractiveElements(root);
    revealSpoilers(root);
    replaceSkilltrees(root, articleIndexMap);

    // Wrapper — article HTML already starts with <h1>, no need to inject another title
    const section = document.createElement('div');
    section.className = 'pdf-article-section markdown-content';
    section.id = `art-${idx}-toc-article`;

    // Move parsed children
    while (root.firstChild) {
      section.appendChild(root.firstChild);
    }

    return section;
  }

  // ── TOC page ──────────────────────────────────────────────────────────────

  function buildTocArticleList(articles: ArticleData[], startIdx: number): HTMLOListElement {
    const ol = document.createElement('ol');
    ol.className = 'pdf-toc-list';
    for (let i = 0; i < articles.length; i++) {
      const li = document.createElement('li');
      li.className = 'pdf-toc-article';
      const a = document.createElement('a');
      a.href = `#art-${startIdx + i}-toc-article`;
      a.textContent = articles[i].title;
      li.appendChild(a);
      ol.appendChild(li);
    }
    return ol;
  }

  function buildTocPage(
    volumeName: string,
    mainArticles: ArticleData[],
    wikiArticles: ArticleData[],
    wikiStartIdx: number,
  ): HTMLDivElement {
    const page = document.createElement('div');
    page.className = 'pdf-toc-page';

    const title = document.createElement('h1');
    title.className = 'pdf-toc-title';
    title.textContent = volumeName;
    page.appendChild(title);

    page.appendChild(buildTocArticleList(mainArticles, 0));

    if (wikiArticles.length > 0) {
      const heading = document.createElement('h2');
      heading.className = 'pdf-toc-section-heading';
      heading.textContent = 'Reference';
      page.appendChild(heading);
      page.appendChild(buildTocArticleList(wikiArticles, wikiStartIdx));
    }

    return page;
  }

  // ── nextFrame helper ──────────────────────────────────────────────────────

  function nextFrame(): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  // ── Main export orchestration ─────────────────────────────────────────────

  let printRoot: HTMLDivElement | null = null;

  async function startExport(volumePath: string, volumeName: string): Promise<void> {
    const m = get(manifest);
    if (!m) {
      pdfStatus.set({ phase: 'error', message: 'Manifest not loaded.' });
      return;
    }

    // Prevent double-start if already running
    const current = get(pdfStatus);
    if (current.phase !== 'idle') return;

    try {
      // 1. Collect article order
      const { mainPaths, wikiPaths } = collectArticleOrder(m.volumes, volumePath);
      const allPaths = [...mainPaths, ...wikiPaths];
      if (allPaths.length === 0) {
        pdfStatus.set({ phase: 'error', message: 'No articles found in this volume.' });
        return;
      }

      // 2. Fetch
      pdfStatus.set({ phase: 'fetching', done: 0, total: allPaths.length });
      const articles = await fetchAllArticles(allPaths);
      const mainArticles = articles.slice(0, mainPaths.length);
      const wikiArticles = articles.slice(mainPaths.length);

      // 3. Build articleIndexMap (key: "#/volumes/..." → index)
      pdfStatus.set({ phase: 'rendering' });
      const articleIndexMap = new Map<string, number>();
      for (let i = 0; i < allPaths.length; i++) {
        articleIndexMap.set('#/' + allPaths[i], i);
      }

      // 4. Create print root
      printRoot = document.createElement('div');
      printRoot.id = 'pdf-print-root';

      // 5. TOC page
      printRoot.appendChild(buildTocPage(volumeName, mainArticles, wikiArticles, mainPaths.length));

      // 6. Article sections
      for (let i = 0; i < articles.length; i++) {
        printRoot.appendChild(buildArticleSection(articles[i], i, articleIndexMap));
      }

      // 7. Embed images
      await embedImages(printRoot);

      // 8. Mount and activate
      document.body.appendChild(printRoot);
      document.documentElement.classList.add('pdf-exporting');

      // 9. Register cleanup BEFORE window.print() — in blocking implementations
      //    afterprint fires synchronously inside window.print(), so the listener
      //    must already be registered or it will be missed.
      const cleanup = () => {
        clearTimeout(fallbackTimer);
        if (printRoot) {
          printRoot.remove();
          printRoot = null;
        }
        document.documentElement.classList.remove('pdf-exporting');
        resetPdfExport();
      };

      const fallbackTimer = setTimeout(cleanup, 30_000);
      window.addEventListener('afterprint', cleanup, { once: true });

      // 10. Print
      pdfStatus.set({ phase: 'printing' });
      await nextFrame();
      window.print();

    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      pdfStatus.set({ phase: 'error', message: msg });
      if (printRoot) {
        printRoot.remove();
        printRoot = null;
      }
      document.documentElement.classList.remove('pdf-exporting');
    }
  }

  function dismissError() {
    resetPdfExport();
  }
</script>

{#if $pdfStatus.phase !== 'idle'}
  <div class="pdf-overlay" role="status" aria-live="polite">
    <div class="pdf-overlay-card">
      {#if $pdfStatus.phase === 'fetching'}
        <p class="pdf-overlay-label">{__STRINGS__.pdfExport.loadingArticles}</p>
        <div class="pdf-progress-bar">
          <div
            class="pdf-progress-fill"
            style="width: {Math.round(($pdfStatus.done / $pdfStatus.total) * 100)}%"
          ></div>
        </div>
        <p class="pdf-overlay-sub">{$pdfStatus.done} / {$pdfStatus.total}</p>

      {:else if $pdfStatus.phase === 'rendering'}
        <p class="pdf-overlay-label">{__STRINGS__.pdfExport.assembling}</p>
        <div class="pdf-spinner"></div>

      {:else if $pdfStatus.phase === 'printing'}
        <p class="pdf-overlay-label">{__STRINGS__.pdfExport.openingPrint}</p>
        <div class="pdf-spinner"></div>

      {:else if $pdfStatus.phase === 'error'}
        <p class="pdf-overlay-label pdf-overlay-error">{__STRINGS__.pdfExport.exportFailed}</p>
        <p class="pdf-overlay-sub">{$pdfStatus.message}</p>
        <button class="pdf-dismiss-btn" on:click={dismissError}>{__STRINGS__.pdfExport.dismiss}</button>
      {/if}
    </div>
  </div>
{/if}

<style>
  .pdf-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.55);
  }

  .pdf-overlay-card {
    background: var(--color-card-bg, #fff);
    color: var(--color-text, #333);
    border-radius: 8px;
    padding: 2rem 2.5rem;
    min-width: 280px;
    max-width: 360px;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  }

  .pdf-overlay-label {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 1rem;
  }

  .pdf-overlay-error {
    color: var(--color-danger, #c0392b);
  }

  .pdf-overlay-sub {
    font-size: 0.875rem;
    color: var(--color-text-muted, #666);
    margin: 0.5rem 0;
  }

  .pdf-progress-bar {
    width: 100%;
    height: 8px;
    background: var(--color-border, #e0e0e0);
    border-radius: 4px;
    overflow: hidden;
    margin: 0.5rem 0;
  }

  .pdf-progress-fill {
    height: 100%;
    background: var(--color-primary, #e94560);
    border-radius: 4px;
    transition: width 0.2s ease;
  }

  .pdf-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid var(--color-border, #e0e0e0);
    border-top-color: var(--color-primary, #e94560);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    margin: 1rem auto 0;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .pdf-dismiss-btn {
    margin-top: 1rem;
    padding: 0.5rem 1.25rem;
    background: var(--color-primary, #e94560);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
  }

  .pdf-dismiss-btn:hover {
    opacity: 0.85;
  }
</style>
